import React, { useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { parse } from "qs";
import { camelizeKeys } from "humps";
import isEqual from "react-fast-compare";
import { BasicBlock } from "main/javascripts/components/block/BasicBlock";
import { MainBlock } from "main/javascripts/components/block/MainBlock";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import {
  cruiseSelector,
  isCabinRatesLoadingSelector,
  isImageModalDisplayedSelector,
  setIsCruiseImageModalDisplayed,
} from "../cruiseSlice";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { CruiseRouteListBlock } from "../../../components/cruise/CruiseRouteListBlock";
import { CruiseVesselAttractions } from "./CruiseVesselAttractions";
import { CruiseVesselInfo } from "./CruiseVesselInfo";
import { CruiseImageList } from "./CruiseImageList";
import { CruiseVesselImageModal } from "./CruiseVesselImageModal";
import { CruiseCabinSearchForm } from "./CruiseCabinSearchForm";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { fontSize } from "main/javascripts/styles/base/typographyStyle";
import {
  convertCruiseShowParamToSearchCabinParam,
  cruiseShowParamSelector,
  initCruiseShowParam,
} from "main/javascripts/slices/paramSlice";
import { CruiseCabinList } from "./CruiseCabinList";
import { SearchLabeledIndicator } from "../../../components/search/SearchLabeledIndicator";
import { CruiseDestinationRegionList } from "./CruiseDestinationRegionList";
import { SimilarCruiseList } from "./SimilarCruiseList";
import { CruiseContentTitle } from "main/javascripts/components/cruise/CruiseContentTitle";
import { CruiseCabinFooterCard } from "./CruiseCabinFooterCard";

export const CruiseShowMain: React.FC = (): React.ReactElement => {
  const { t } = useTranslation(["component"]);
  const appDispatch = useAppDispatch();
  const router = useRouter();
  const urlParams = parse(router.asPath.split("?")[1]);

  const cruise = useAppSelector(cruiseSelector);
  const isImageModalDisplayed = useAppSelector(isImageModalDisplayedSelector);
  const isLoading = useAppSelector(isCabinRatesLoadingSelector);
  const param = useAppSelector(cruiseShowParamSelector);

  const prevUrlParamsRef = useRef(urlParams);

  // ブラウザ履歴からの遷移等でURLクエリのみ変化した場合
  // パラメータを更新して再フェッチをトリガーする
  useEffect(() => {
    if (!isEqual(prevUrlParamsRef.current, urlParams)) {
      const camelizedKeysParams = camelizeKeys(urlParams);
      if (
        !isEqual(
          convertCruiseShowParamToSearchCabinParam(param),
          convertCruiseShowParamToSearchCabinParam(camelizedKeysParams)
        )
      ) {
        appDispatch(initCruiseShowParam(camelizedKeysParams));
      }
    }
    prevUrlParamsRef.current = urlParams;
  }, [urlParams]);

  const onImageModalClose = () => {
    appDispatch(setIsCruiseImageModalDisplayed(false));
  };

  const routeListRef = useRef<HTMLDivElement>(null);
  const [isRouteStickyEnabled, setIsRouteStickyEnabled] = useState(true);

  useEffect(() => {
    const checkStickyEligibility = () => {
      const isDesktopSize = window.matchMedia("(min-width: 960px)").matches;

      if (!isDesktopSize) {
        setIsRouteStickyEnabled(false);
        return;
      }

      if (routeListRef.current) {
        setIsRouteStickyEnabled(
          routeListRef.current.offsetHeight < window.innerHeight
        );
      }
    };

    checkStickyEligibility();
    window.addEventListener("resize", checkStickyEligibility);
    return () => window.removeEventListener("resize", checkStickyEligibility);
  }, []);

  return (
    <MainBlock>
      <CruiseImageList />
      <BasicBlock styleKey="blockMultiColWide">
        <div css={headerStyle}>
          <h1 css={titleStyle}>{cruise.title}</h1>
          <div css={dataStyle}>
            <div>
              {t("cruise.departurePort")}: {cruise.departurePortName}
            </div>
            <div>
              {t("cruise.nights")}: {cruise.stays}
            </div>
            <div>
              {cruise.vessel.name} / {cruise.vessel.vesselBrand.name}
            </div>
          </div>
        </div>

        <div css={cabinColumnStyle}>
          <div
            css={[
              routeListBaseStyle,
              isRouteStickyEnabled && routeListStickyStyle,
            ]}
            ref={routeListRef}
          >
            <CruiseRouteListBlock cruise={cruise} />
          </div>
          <div css={searchFormBaseStyle}>
            <CruiseContentTitle>{t("cruise.cabin")}</CruiseContentTitle>
            <div css={searchFormBlockStyle}>
              <CruiseCabinSearchForm />
              <div css={labeledIndicatorBlockStyle}>
                <div css={labeledIndicatorStyle}>
                  <SearchLabeledIndicator
                    text={t("search.searchingCruiseCabins")}
                    isLoading={isLoading}
                  />
                </div>
              </div>
            </div>
            <div css={cabinListStyle}>
              <CruiseCabinList />
            </div>
          </div>
        </div>
      </BasicBlock>

      <BasicBlock styleKey="blockMultiColWide">
        <div css={[columnStyle, lastBlockStyle]}>
          <CruiseVesselAttractions />
          <CruiseVesselInfo />
          <CruiseVesselImageModal
            vessel={cruise.vessel}
            isDisplayed={isImageModalDisplayed}
            onClose={onImageModalClose}
          />
        </div>
      </BasicBlock>

      <BasicBlock styleKey="blockWide">
        <div css={similarCruisesBlockStyle}>
          <SimilarCruiseList />
        </div>
        <div css={destinationRegionsBlockStyle}>
          <CruiseDestinationRegionList />
        </div>
      </BasicBlock>
      <CruiseCabinFooterCard />
    </MainBlock>
  );
};

const headerStyle = css`
  padding: ${space.atom2x} ${space.atom2x};
`;
const dataStyle = css`
  padding: ${space.atom2x} 0;
`;
const titleStyle = css`
  font-size: 1.625rem;
  line-height: 1.4;
  ${moreThanBreakpoint("tablet")} {
    font-size: 1.875rem;
  }
  ${moreThanBreakpoint("desktop")} {
    font-size: ${fontSize.large};
  }
`;
const cabinColumnStyle = css`
  padding: ${space.atom4x} ${space.atom2x};
  display: grid;
  grid-template-columns: 100%;
  gap: 4rem;
  ${moreThanBreakpoint("desktop")} {
    padding: ${space.atom4x} ${space.atom2x};
    grid-template-columns: minmax(0, 0.45fr) minmax(0, 0.55fr);
    gap: 4%;
    align-items: flex-start;
  }
`;
const columnStyle = css`
  padding: ${space.atom4x} ${space.atom2x};
  display: grid;
  grid-template-columns: 100%;
  gap: 4rem;
  ${moreThanBreakpoint("desktop")} {
    grid-template-columns: minmax(0, 0.55fr) minmax(0, 0.45fr);
    gap: 4%;
  }
`;
const similarCruisesBlockStyle = css`
  padding: ${space.atom4x} ${space.atom};
`;
const destinationRegionsBlockStyle = css`
  padding: ${space.atom4x} ${space.atom} 6rem;
`;
const routeListBaseStyle = css`
  position: static;
`;
const routeListStickyStyle = css`
  position: sticky;
  top: ${space.atom2x};
`;
const searchFormBaseStyle = css`
  position: static;
  padding: 0 0 3rem 0;
`;
const searchFormBlockStyle = css`
  position: relative;
  padding-bottom: 2rem;
`;
const labeledIndicatorBlockStyle = css`
  position: absolute;
  bottom: 0.375rem;
  left: 50%;
  transform: translateX(-50%);
  ${moreThanBreakpoint("tablet")} {
    bottom: 0.625rem;
  }
`;
const labeledIndicatorStyle = css`
  display: flex;
  justify-content: center;
`;
const cabinListStyle = css`
  padding-top: ${space.atom};
`;
const lastBlockStyle = css`
  padding-bottom: 4rem;
`;
